import './style.css';
import { translateDom } from './i18n.ts';
import { Splash } from './Splash.ts';
translateDom();
const splash = new Splash();
import {
  ARAM_TEAM_SIZES,
  DEFAULT_MATCH_CONFIG,
  HERO_ORDER,
  defaultRoster,
  heroStats,
  TEAMS,
  getHeroDef,
  type HeroId,
  type PresenceStatus,
  type LobbyState,
  type MatchConfig,
  type Team,
} from '@moba/shared';
import { Game, type OfflineKind } from './Game.ts';
import { NetClient } from './NetClient.ts';
import { AccountClient, httpEndpoint } from './Account.ts';
import { SocialClient } from './SocialClient.ts';
import { SocialUi } from './SocialUi.ts';
import { ChatUi } from './Chat.ts';
import { EmoteWheel } from './EmoteWheel.ts';
import { KIT_DESCRIPTIONS, KIT_NAMES, abilityIcon } from './abilityIcons.ts';
import { Gallery, HERO_ROLES } from './Gallery.ts';


import { LANG, saveLanguage, t, tr, matchLabelT, modeCode, type Lang } from './i18n.ts';
const TEAM_NAMES: Record<Team, string> = { blue: t('AZUL'), red: t('ROJO') };
/** retratos (busto) de cada héroe, generados al cargar */
let portraits = new Map<HeroId, string>();

/** Ficha del héroe elegido: retrato, rol, vida/alcance y su kit con descripciones. */
function renderKit(container: HTMLElement, heroId: HeroId): void {
  container.replaceChildren();
  const def = getHeroDef(heroId);
  const stats = heroStats(heroId, 1);
  const head = document.createElement('div');
  head.className = 'kit-head';
  const portrait = portraits.get(heroId);
  if (portrait) {
    const img = document.createElement('img');
    img.src = portrait;
    img.alt = '';
    head.appendChild(img);
  }
  const title = document.createElement('div');
  title.className = 'kit-title';
  title.innerHTML = `<b>${def.displayName}</b><small>${HERO_ROLES[heroId]} · ${def.ranged ? t('A distancia') : t('Cuerpo a cuerpo')} · ${t('{hp} de vida', { hp: stats.maxHp })} · ${t('{armor} de armadura', { armor: stats.armor })}</small>`;
  head.appendChild(title);
  container.appendChild(head);

  const list = document.createElement('div');
  list.className = 'kit-list';
  for (const slot of ['q', 'w', 'e', 'r'] as const) {
    const row = document.createElement('div');
    row.className = 'kit-ability';
    const icon = document.createElement('img');
    icon.src = abilityIcon(heroId, slot);
    icon.alt = '';
    const text = document.createElement('div');
    const name = document.createElement('b');
    name.innerHTML = `<span class="kit-key">${slot.toUpperCase()}</span>`;
    name.append(KIT_NAMES[heroId][slot].replace(/ \(.*\)$/, ''));
    const description = document.createElement('small');
    description.textContent = KIT_DESCRIPTIONS[heroId][slot] ?? '';
    text.append(name, description);
    row.append(icon, text);
    list.appendChild(row);
  }
  container.appendChild(list);
}

function el<T extends HTMLElement = HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Falta #${id} en index.html`);
  return element as T;
}

const canvas = el<HTMLCanvasElement>('game');
const overlay = el('overlay');
const overlayCard = el('overlay-card');
const teamsLabel = el('teams-label');
const menuRoster = el('menu-roster');
const startButton = el<HTMLButtonElement>('start-btn');
const restartButton = el<HTMLButtonElement>('restart-btn');
const loadingFill = el('loading-fill');
const loadingLabel = el('loading-label');
const modeKicker = el('mode-kicker');
const modeSub = el('mode-sub');
const screens = {
  menu: el('screen-menu'),
  connect: el('screen-connect'),
  lobby: el('screen-lobby'),
  gallery: el('screen-gallery'),
};
const menuOffline = el<HTMLButtonElement>('menu-offline');
const menuOnline = el<HTMLButtonElement>('menu-online');
const menuGallery = el<HTMLButtonElement>('menu-gallery');
/** galería de personajes (fuera de partida), creada al terminar de cargar */
let gallery: Gallery | undefined;
const nameInput = el<HTMLInputElement>('player-name-input');
const codeInput = el<HTMLInputElement>('room-code-input');
const connectStatus = el('connect-status');
const lobbyRoom = el('lobby-room');
const modePicker = el('mode-picker');
const lobbyTeams = el('lobby-teams');
const lobbyError = el('lobby-error');
const heroSelect = el('hero-select');
const heroInfo = el('hero-select-info');

const game = new Game(canvas);
const heroCards = new Map<HeroId, HTMLButtonElement>();

/** Chat de la sala (lobby online) y de la partida (abajo a la derecha, Enter). */
const chat = new ChatUi({
  send: (text, scope) => {
    if (net && lobbyKind === 'online') net.sendChat(text, scope);
    else chat.pushLocal('Tú', text, scope, 'blue');
  },
  canOpenInGame: () =>
    started &&
    overlay.classList.contains('hidden') &&
    (pauseMenu?.hidden ?? true) &&
    !settingsOpen(),
});

/** Rueda de emojis: mantener T, arrastrar hacia el emoji y soltar. */
const emoteWheel = new EmoteWheel({
  canOpen: () => game.canEmote && overlay.classList.contains('hidden') && !chat.isTyping && !settingsOpen(),
  onPick: (emote) => game.sendEmote(emote),
});

/** En partida (overlay oculto): habilita la pausa. */
let started = false;
let selectedHero: HeroId = 'knight';
/** Lobby abierto: offline (contra bots) u online (sala del servidor). */
let lobbyKind: 'offline' | 'online' = 'offline';
let offlineKind: OfflineKind = 'match';
let offlineConfig: MatchConfig = { ...DEFAULT_MATCH_CONFIG };
let net: NetClient | undefined;
let lobby: LobbyState | undefined;

declare global {
  interface Window {
    __moba?: { game: Game };
  }
}
window.__moba = { game };

// ----------------------------------------------------------------- pantallas

type Screen = keyof typeof screens;

function showScreen(screen: Screen): void {
  for (const [name, element] of Object.entries(screens)) element.hidden = name !== screen;
  // cada pantalla tiene su tamaño (el lobby es ancho, a dos columnas)
  overlayCard.dataset.screen = screen;
  lobbyError.textContent = '';
  if (screen !== 'gallery') gallery?.hide();
  if (screen === 'gallery') {
    modeKicker.textContent = t('PERSONAJES');
    modeSub.textContent = t('Habilidades, animaciones y estadísticas de cada héroe');
  } else if (screen === 'menu') {
    modeKicker.textContent = 'MELEE MOBA';
    modeSub.textContent = t('Elige cómo quieres jugar');
  } else if (screen === 'connect') {
    void measureServerLatency();
    modeKicker.textContent = 'ONLINE';
    modeSub.textContent = t('Crea una sala o únete a la de un amigo');
  }
}

function showOverlay(screen: Screen): void {
  started = false;
  chat.setGameVisible(false);
  overlay.classList.remove('hidden');
  showScreen(screen);
}

function setProgress(fraction: number, label: string): void {
  loadingFill.style.width = `${Math.round(fraction * 100)}%`;
  loadingLabel.textContent = label;
  splash.setProgress(fraction, label);
}

function setLoadingError(message: string): void {
  loadingLabel.textContent = message;
  loadingLabel.style.color = '#ff7a6b';
}

// ----------------------------------------------------------- héroes (lobby)

function selectHero(heroId: HeroId): void {
  selectedHero = heroId;
  for (const [id, card] of heroCards) card.classList.toggle('selected', id === heroId);
  renderKit(heroInfo, heroId);
  if (lobbyKind === 'online') net?.sendLobby({ type: 'hero', heroId });
  else renderOfflineLobby();
}

for (const heroId of HERO_ORDER) {
  const card = document.createElement('button');
  card.type = 'button';
  card.className = 'hero-card';
  const name = getHeroDef(heroId).displayName;
  card.innerHTML = `<span class="placeholder">${name[0]}</span><span class="hero-name">${name}</span><span class="hero-role">${HERO_ROLES[heroId]}</span>`;
  card.addEventListener('click', () => selectHero(heroId));
  card.addEventListener('dblclick', () => {
    if (lobbyKind !== 'offline') return;
    selectHero(heroId);
    void startOffline();
  });
  heroSelect.appendChild(card);
  heroCards.set(heroId, card);
}
for (const [id, card] of heroCards) card.classList.toggle('selected', id === selectedHero);
renderKit(heroInfo, selectedHero);

// ------------------------------------------------------------ selector de modo

interface ModeOption {
  label: string;
  hint: string;
  glyph: 'lanes' | 'aram' | 'training';
  active: boolean;
  onPick: () => void;
}

/** Mini-mapa de cada modo (azul abajo a la izquierda, rojo arriba a la derecha, como el minimapa). */
function modeGlyph(kind: ModeOption['glyph']): string {
  const lane = (d: string) => `<path d="${d}" stroke="#cfd6de" stroke-width="4" fill="none" stroke-linecap="round" stroke-linejoin="round"/>`;
  const bases = '<circle cx="11" cy="33" r="4" fill="#4da3ff"/><circle cx="33" cy="11" r="4" fill="#ff5a5a"/>';
  const ring = '<circle cx="22" cy="22" r="20" fill="#16331c" stroke="rgba(255,255,255,0.18)"/>';
  let body = '';
  if (kind === 'lanes') body = lane('M11 33 L11 11 L33 11') + lane('M11 33 L33 11') + lane('M11 33 L33 33 L33 11') + bases;
  else if (kind === 'aram') body = lane('M11 33 L33 11') + bases;
  else body = '<circle cx="22" cy="22" r="11" fill="none" stroke="#ffd166" stroke-width="3"/><circle cx="22" cy="22" r="4.5" fill="#ff5a5a"/>';
  return `<svg viewBox="0 0 44 44" width="40" height="40" aria-hidden="true">${ring}${body}</svg>`;
}

/** Botones del modo (3v3 de tres líneas / ARAM) y del tamaño de equipo de ARAM. */
function renderModePicker(config: MatchConfig, editable: boolean, training: boolean, onChange: (config: MatchConfig | 'training') => void): void {
  modePicker.replaceChildren();
  const row = document.createElement('div');
  row.className = 'mode-row';
  const options: ModeOption[] = [
    {
      label: t('3v3 · Tres líneas'),
      hint: t('Un héroe por línea, torres y minions en las tres'),
      glyph: 'lanes',
      active: !training && config.mode === 'lanes',
      onPick: () => onChange({ mode: 'lanes', teamSize: 3 }),
    },
    {
      label: 'ARAM',
      hint: t('Una sola línea: todos pelean en el centro'),
      glyph: 'aram',
      active: !training && config.mode === 'aram',
      onPick: () => onChange({ mode: 'aram', teamSize: config.mode === 'aram' ? config.teamSize : 3 }),
    },
  ];
  if (lobbyKind === 'offline') {
    options.push({ label: t('Entrenamiento'), hint: t('Muñecos de práctica, sin partida'), glyph: 'training', active: training, onPick: () => onChange('training') });
  }
  for (const option of options) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'mode-option';
    button.classList.toggle('active', option.active);
    button.disabled = !editable;
    button.innerHTML = `${modeGlyph(option.glyph)}<span><b>${option.label}</b><small>${option.hint}</small></span>`;
    button.addEventListener('click', option.onPick);
    row.appendChild(button);
  }
  modePicker.appendChild(row);

  if (!training && config.mode === 'aram') {
    const sizes = document.createElement('div');
    sizes.className = 'size-row';
    for (const size of ARAM_TEAM_SIZES) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'size-option';
      button.classList.toggle('active', config.teamSize === size);
      button.disabled = !editable;
      button.textContent = `${size}v${size}`;
      button.addEventListener('click', () => onChange({ mode: 'aram', teamSize: size }));
      sizes.appendChild(button);
    }
    modePicker.appendChild(sizes);
  }
}

// ----------------------------------------------------------------- equipos

interface SlotView {
  name: string;
  heroId?: HeroId;
  detail: string;
  me?: boolean;
  bot?: boolean;
}

/** Columnas azul/roja con un puesto por héroe; `extra` añade botones al pie de cada una. */
function renderTeams(columns: Record<Team, SlotView[]>, extra?: (team: Team, column: HTMLElement) => void): void {
  lobbyTeams.hidden = false;
  teamsLabel.hidden = false;
  lobbyTeams.replaceChildren();
  for (const team of TEAMS) {
    const column = document.createElement('div');
    column.className = `lobby-team ${team}`;
    const title = document.createElement('div');
    title.className = 'lobby-team-title';
    title.textContent = t('EQUIPO {team}', { team: TEAM_NAMES[team] });
    column.appendChild(title);
    for (const slot of columns[team]) {
      const row = document.createElement('div');
      row.className = 'lobby-slot';
      row.classList.toggle('me', Boolean(slot.me));
      row.classList.toggle('bot', Boolean(slot.bot));
      const portrait = slot.heroId ? portraits.get(slot.heroId) : undefined;
      if (portrait) {
        const img = document.createElement('img');
        img.src = portrait;
        img.alt = '';
        row.appendChild(img);
      } else {
        const blank = document.createElement('span');
        blank.className = 'slot-blank';
        blank.textContent = '?';
        row.appendChild(blank);
      }
      const label = document.createElement('span');
      label.className = 'slot-name';
      label.textContent = slot.name;
      const detail = document.createElement('small');
      detail.textContent = slot.detail;
      row.append(label, detail);
      column.appendChild(row);
    }
    extra?.(team, column);
    lobbyTeams.appendChild(column);
  }
}

function hideTeams(): void {
  lobbyTeams.hidden = true;
  teamsLabel.hidden = true;
}

// ------------------------------------------------------------- lobby offline

function openOfflineLobby(): void {
  lobbyKind = 'offline';
  chat.setLobbyVisible(false);
  lobbyRoom.hidden = true;
  showScreen('lobby');
  renderOfflineLobby();
}

function renderOfflineLobby(): void {
  const training = offlineKind === 'training';
  modeKicker.textContent = 'OFFLINE';
  modeSub.textContent = `${training ? t('Entrenamiento') : t('{mode} contra bots', { mode: matchLabelT(offlineConfig) })} · ${getHeroDef(selectedHero).displayName}`;
  renderModePicker(offlineConfig, true, training, (choice) => {
    if (choice === 'training') {
      offlineKind = 'training';
    } else {
      offlineKind = 'match';
      offlineConfig = choice;
    }
    renderOfflineLobby();
  });
  // vista previa de los equipos: tú en azul y bots en el resto de puestos
  if (training) {
    hideTeams();
  } else {
    const roster = defaultRoster(offlineConfig, selectedHero);
    const columns = {} as Record<Team, SlotView[]>;
    for (const team of TEAMS) {
      columns[team] = roster[team].map((heroId, slot) => {
        const me = team === 'blue' && slot === 0;
        return { name: me ? t('Tú') : `${getHeroDef(heroId).displayName}`, heroId, detail: me ? getHeroDef(heroId).displayName : 'Bot', me, bot: !me };
      });
    }
    renderTeams(columns);
  }
  startButton.textContent = training ? t('ENTRAR AL ENTRENAMIENTO') : t('EMPEZAR PARTIDA');
  startButton.disabled = false;
}

async function startOffline(): Promise<void> {
  if (started || lobbyKind !== 'offline') return;
  started = true;
  window.addEventListener('beforeunload', confirmLeave);
  startButton.blur();
  game.startOffline(offlineKind, offlineConfig, selectedHero);
  overlay.classList.add('hidden');
  chat.clear();
  chat.setGameVisible(true);
}

// -------------------------------------------------------------- lobby online


function setConnectBusy(busy: boolean, message = ''): void {
  for (const id of ['online-create', 'online-join', 'online-quick']) el<HTMLButtonElement>(id).disabled = busy;
  connectStatus.textContent = message;
  connectStatus.classList.remove('error');
}

function newNetClient(): NetClient {
  const client = new NetClient({
    token: () => (account.loggedIn ? account.token : ''),
    onStatus: (status, detail) => {
      game.netStatusHandler(status, detail);
      if (status === 'closed' && net === client) {
        net = undefined;
        lobby = undefined;
        chat.clear();
        chat.setLobbyVisible(false);
        if (started) game.stopOnline();
        showOverlay('connect');
        connectStatus.textContent = t('Se perdió la conexión con la sala ({detail})', { detail: detail ?? t('cerrada') });
        connectStatus.classList.add('error');
      }
    },
    onLobby: (state) => onLobbyState(state),
    onLobbyError: (message) => {
      lobbyError.textContent = tr(message);
    },
    onWelcome: (welcome) => {
      lobbyError.textContent = '';
      game.startOnline(client, welcome);
      started = true;
      window.addEventListener('beforeunload', confirmLeave);
      overlay.classList.add('hidden');
      chat.setGameVisible(true);
    },
    onChat: (messages) => chat.push(messages),
    onEmote: (message) => {
      if (started) game.showEmote(message.heroId, message.emote);
    },
    onSnapshot: (snapshot) => game.pushSnapshot(snapshot),
  });
  return client;
}

async function connectOnline(action: 'create' | 'join' | 'quick'): Promise<void> {
  // el servidor decide el nombre: el de la cuenta o uno aleatorio de invitado
  const name = account.loggedIn ? account.user!.username : '';
  const code = roomCodeFrom(codeInput.value);
  if (action === 'join' && !code) {
    connectStatus.textContent = t('Escribe el código de la sala');
    connectStatus.classList.add('error');
    return;
  }
  setConnectBusy(true, t('Conectando…'));
  const client = newNetClient();
  net = client;
  try {
    const state =
      action === 'create'
        ? await client.create(name, selectedHero)
        : action === 'join'
          ? await client.joinByCode(code, name, selectedHero)
          : await client.quickJoin(name, selectedHero);
    setConnectBusy(false);
    lobbyKind = 'online';
    chat.setLobbyVisible(true);
    showScreen('lobby');
    onLobbyState(state);
  } catch (error) {
    if (net === client) net = undefined;
    setConnectBusy(false, error instanceof Error ? tr(error.message) : t('No se pudo conectar'));
    connectStatus.classList.add('error');
  }
}

function onLobbyState(state: LobbyState): void {
  lobby = state;
  if (lobbyKind !== 'online') return;
  // la partida terminó: la sala vuelve al lobby
  if (state.phase === 'lobby' && started) {
    game.stopOnline();
    showOverlay('lobby');
  }
  renderOnlineLobby();
}

function renderOnlineLobby(): void {
  const state = lobby;
  if (!state || !net) return;
  const me = state.players.find((p) => p.sessionId === net!.sessionId);
  const isHost = state.hostId === net.sessionId;
  modeKicker.textContent = t('SALA ONLINE');
  modeSub.textContent = `${matchLabelT(state.config)} · ${state.players.length === 1 ? t('1 jugador') : t('{count} jugadores', { count: state.players.length })}`;

  lobbyRoom.hidden = false;
  lobbyRoom.replaceChildren();
  const code = document.createElement('span');
  code.append(`${t('Código de la sala')} `);
  const codeValue = document.createElement('b');
  codeValue.textContent = state.roomId;
  code.append(codeValue);
  // invitar: el código, un enlace que entra directo a la sala y, en móvil, compartir
  const actionButton = (label: string, onClick: (button: HTMLButtonElement) => void): HTMLButtonElement => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ghost-btn small';
    button.textContent = label;
    button.addEventListener('click', () => onClick(button));
    return button;
  };
  const copyText = (button: HTMLButtonElement, text: string, done: string) => {
    void navigator.clipboard?.writeText(text).then(() => {
      button.textContent = done;
    });
  };
  const link = roomInviteUrl(state.roomId);
  lobbyRoom.append(
    code,
    actionButton(t('Copiar código'), (button) => copyText(button, state.roomId, t('¡Copiado!'))),
    actionButton(t('Copiar enlace'), (button) => copyText(button, link, t('¡Enlace copiado!'))),
  );
  lobbyRoom.append(lobbyPing);
  if (typeof navigator.share === 'function') {
    lobbyRoom.append(
      actionButton(t('Compartir'), () => {
        void navigator.share({ title: 'Melee MOBA', text: t('Únete a mi sala de Melee MOBA'), url: link }).catch(() => {});
      }),
    );
  }

  renderModePicker(state.config, isHost && state.phase === 'lobby', false, (choice) => {
    if (choice !== 'training') net?.sendLobby({ type: 'config', config: choice });
  });

  const columns = {} as Record<Team, SlotView[]>;
  for (const team of TEAMS) {
    const members = state.players.filter((p) => p.team === team);
    columns[team] = Array.from({ length: state.config.teamSize }, (_, slot): SlotView => {
      const player = members[slot];
      if (!player) return { name: t('Libre'), detail: t('Lo llevará un bot'), bot: true };
      const me = player.sessionId === net!.sessionId;
      return {
        name: me ? t('{name} (tú)', { name: player.name }) : player.name,
        heroId: player.heroId,
        detail: `${getHeroDef(player.heroId).displayName}${player.host ? ` · ${t('anfitrión')}` : ''}`,
        me,
      };
    });
  }
  renderTeams(columns, (team, column) => {
    if (!me || me.team === team || state.phase !== 'lobby') return;
    const move = document.createElement('button');
    move.type = 'button';
    move.className = 'ghost-btn small';
    move.textContent = t('Cambiar a {team}', { team: TEAM_NAMES[team].toLowerCase() });
    move.disabled = state.players.filter((p) => p.team === team).length >= state.config.teamSize;
    move.addEventListener('click', () => net?.sendLobby({ type: 'team', team }));
    column.appendChild(move);
  });

  // el héroe que el servidor tiene registrado manda (p. ej. si otro ya lo eligió)
  if (me && me.heroId !== selectedHero) {
    selectedHero = me.heroId;
    for (const [id, card] of heroCards) card.classList.toggle('selected', id === selectedHero);
    renderKit(heroInfo, selectedHero);
  }

  if (state.phase === 'playing') {
    startButton.textContent = t('PARTIDA EN CURSO…');
    startButton.disabled = true;
  } else if (isHost) {
    startButton.textContent = t('EMPEZAR PARTIDA');
    startButton.disabled = false;
  } else {
    startButton.textContent = t('ESPERANDO AL ANFITRIÓN…');
    startButton.disabled = true;
  }
}

// ------------------------------------------------------------ cuentas y amigos

const account = new AccountClient();
let socialUi: SocialUi | undefined;
const social = new SocialClient({
  onState: () => socialUi?.onState(),
  onInvite: (invite) => socialUi?.onInvite(invite),
  onMessage: (message) => socialUi?.onMessage(message),
  onError: (message) => socialUi?.error(message),
  onNotice: (message) => socialUi?.notice(message),
  onConnection: (connected) => socialUi?.setConnected(connected),
  onAuthLost: () => {
    account.expire();
    socialUi?.error(t('Tu sesión caducó. Inicia sesión de nuevo.'));
  },
});
socialUi = new SocialUi({
  account,
  social,
  currentRoom: () => (net && lobby && lobbyKind === 'online' ? { roomId: lobby.roomId, mode: modeCode(lobby.config) } : null),
  joinRoom: (roomId) => joinRoomFromFriends(roomId),
});

/**
 * Con sesión, el nombre online es el de la cuenta; los invitados no eligen
 * nombre (el servidor les da uno aleatorio: animal + 4 dígitos).
 */
function applyAccountName(): void {
  const field = document.getElementById('player-name-field');
  const hint = document.getElementById('guest-name-hint');
  if (field) field.hidden = !account.loggedIn;
  if (hint) hint.hidden = account.loggedIn;
  nameInput.value = account.user?.username ?? '';
  nameInput.title = account.loggedIn ? t('Es el nombre de tu cuenta') : '';
}
document.getElementById('guest-login')?.addEventListener('click', () => socialUi?.openAuth('login'));

account.onChange(() => {
  if (account.loggedIn) social.connect(account.token);
  else social.disconnect();
  applyAccountName();
});
void account.restore();

/** Unirse a la sala de un amigo (o aceptar su invitación). */
function joinRoomFromFriends(roomId: string): string | null {
  if (started) return t('Termina o abandona la partida actual primero');
  if (net && lobby?.roomId === roomId) return null;
  if (net) leaveOnlineLobby();
  showOverlay('connect');
  codeInput.value = roomId;
  void connectOnline('join');
  return null;
}

/** Qué estoy haciendo, para la lista de amigos de los demás. */
function currentPresence(): PresenceStatus {
  if (started) {
    if (game.isOnline && lobby) return { state: 'match', roomId: lobby.roomId, mode: modeCode(lobby.config), heroId: selectedHero };
    return { state: 'offline-match', mode: modeCode(offlineKind === 'training' ? 'training' : offlineConfig), heroId: selectedHero };
  }
  if (net && lobby && lobbyKind === 'online') return { state: 'lobby', roomId: lobby.roomId, mode: modeCode(lobby.config) };
  return { state: 'menu' };
}
window.setInterval(() => {
  if (account.loggedIn) social.setStatus(currentPresence());
}, 1000);

/** Enlace que abre el juego y entra directo a la sala (conserva ?server= en desarrollo). */
function roomInviteUrl(roomId: string): string {
  const url = new URL(window.location.pathname, window.location.origin);
  const server = new URLSearchParams(window.location.search).get('server');
  if (server) url.searchParams.set('server', server);
  url.searchParams.set('room', roomId);
  return url.toString();
}

/** Código de sala desde un código o un enlace pegado ("…/?room=abc"). */
function roomCodeFrom(raw: string): string {
  const text = raw.trim();
  const match = /[?&]room=([A-Za-z0-9_-]{1,32})/.exec(text);
  return match ? match[1] : text;
}

// ------------------------------------------------------------ latencia
type PingQuality = 'good' | 'fair' | 'bad' | 'unknown';
const pingQuality = (ms: number): PingQuality => (ms <= 0 ? 'unknown' : ms < 80 ? 'good' : ms < 150 ? 'fair' : 'bad');

function setPingChip(chip: HTMLElement, ms: number | null, label: string): void {
  chip.hidden = false;
  chip.dataset.quality = ms === null ? 'unknown' : pingQuality(ms);
  const value = chip.querySelector('b');
  if (value) value.textContent = label;
}

const connectPing = document.getElementById('connect-ping');
let measuringLatency = false;

/**
 * Latencia al servidor antes de entrar a una sala: una petición de calentamiento
 * (abre la conexión TCP/TLS, que costaría varias idas y vueltas) y el mínimo de
 * las 3 siguientes a /health, que reutilizan la conexión.
 */
async function measureServerLatency(): Promise<void> {
  if (!connectPing || measuringLatency) return;
  measuringLatency = true;
  setPingChip(connectPing, null, t('Midiendo latencia…'));
  const samples: number[] = [];
  for (let i = 0; i < 4; i++) {
    const startedAt = performance.now();
    try {
      const response = await fetch(`${httpEndpoint()}/health`, { cache: 'no-store' });
      if (response.ok && i > 0) samples.push(performance.now() - startedAt);
    } catch {
      // servidor caído: se informa abajo
    }
  }
  measuringLatency = false;
  if (samples.length === 0) {
    setPingChip(connectPing, null, t('Servidor no disponible'));
    connectPing.dataset.quality = 'bad';
    return;
  }
  const ms = Math.round(Math.min(...samples));
  setPingChip(connectPing, ms, t('Latencia al servidor: {ms} ms', { ms }));
}

/** Ping de la sala en el lobby online (el mismo que mide la partida), cada segundo. */
const lobbyPing = document.createElement('span');
lobbyPing.className = 'ping-chip';
lobbyPing.title = t('Ida y vuelta al servidor');
lobbyPing.innerHTML = '<i></i><b></b>';
window.setInterval(() => {
  if (!net || !lobby || lobbyKind !== 'online' || screens.lobby.hidden) return;
  const ms = Math.round(net.ping);
  setPingChip(lobbyPing, ms > 0 ? ms : null, ms > 0 ? t('Ping {ms} ms', { ms }) : t('Midiendo latencia…'));
  if (!lobbyPing.isConnected) lobbyRoom.append(lobbyPing);
}, 1000);

function leaveOnlineLobby(): void {
  const client = net;
  net = undefined;
  lobby = undefined;
  client?.leave();
  chat.clear();
  chat.setLobbyVisible(false);
  lobbyKind = 'offline';
  showScreen('connect');
}

// ----------------------------------------------------------------- arranque

/**
 * Pantalla completa opcional (desde ajustes). En fullscreen se captura el teclado
 * con Keyboard Lock para que atajos del navegador como Ctrl+W no cierren la
 * partida; Esc mantenido sale. Fuera de fullscreen se juega en ventana.
 * Debe llamarse dentro del gesto del usuario (clic).
 */
function setFullscreen(active: boolean): void {
  const keyboard = (navigator as Navigator & { keyboard?: { lock?: (codes?: string[]) => Promise<void>; unlock?: () => void } }).keyboard;
  if (active) {
    if (document.fullscreenElement) return;
    document.documentElement
      .requestFullscreen?.()
      // sin argumentos: captura TODAS las teclas; Esc mantenido sale del fullscreen
      .then(() => keyboard?.lock?.())
      .catch((error: unknown) => console.warn('[input] sin pantalla completa / keyboard lock', error));
    return;
  }
  keyboard?.unlock?.();
  if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
}

function refreshFullscreenButton(): void {
  const button = document.getElementById('setting-fullscreen');
  if (!button) return;
  // interruptor: encendido mientras el juego está en pantalla completa
  button.setAttribute('aria-checked', String(Boolean(document.fullscreenElement)));
}

/** Red de seguridad: si algo intenta cerrar/recargar la pestaña en partida, preguntar antes. */
function confirmLeave(event: BeforeUnloadEvent): void {
  event.preventDefault();
  event.returnValue = '';
}

function start(): void {
  if (screens.lobby.hidden || startButton.disabled) return;
  if (lobbyKind === 'offline') {
    void startOffline();
    return;
  }
  // online: el anfitrión arranca la sala; la partida empieza al llegar la bienvenida
  startButton.disabled = true;
  net?.sendLobby({ type: 'start' });
}

/** Fila de retratos de los seis luchadores en el menú. */
function renderMenuRoster(): void {
  menuRoster.replaceChildren();
  for (const heroId of HERO_ORDER) {
    const portrait = portraits.get(heroId);
    if (!portrait) continue;
    const figure = document.createElement('figure');
    const img = document.createElement('img');
    img.src = portrait;
    img.alt = '';
    const caption = document.createElement('figcaption');
    caption.textContent = getHeroDef(heroId).displayName;
    figure.append(img, caption);
    menuRoster.appendChild(figure);
  }
}

async function boot(): Promise<void> {
  try {
    await game.init(setProgress);
    try {
      portraits = game.renderPortraits();
      for (const [heroId, url] of portraits) {
        const card = heroCards.get(heroId);
        const placeholder = card?.querySelector('.placeholder');
        if (!card || !placeholder) continue;
        const img = document.createElement('img');
        img.src = url;
        img.alt = getHeroDef(heroId).displayName;
        placeholder.replaceWith(img);
      }
    } catch (error) {
      console.warn('[select] no se pudieron generar los retratos', error);
    }
    loadingFill.style.width = '100%';
    loadingLabel.textContent = t('Listo');
    overlay.classList.add('loaded');
    void splash.finish();
    renderMenuRoster();
    renderKit(heroInfo, selectedHero);
    menuOffline.disabled = false;
    menuOnline.disabled = false;
    gallery = new Gallery(el('gallery'), game.characterAssets, portraits, (heroId, clipKey) => game.previewClipSound(heroId, clipKey));
    menuGallery.disabled = false;
    // atajos por URL: ?room=CÓDIGO (unirse a esa sala) · ?mode=training · ?mode=online · ?mode=heroes
    const params = new URLSearchParams(window.location.search);
    const requested = params.get('mode');
    const roomParam = params.get('room');
    if (roomParam && /^[A-Za-z0-9_-]{1,32}$/.test(roomParam)) {
      // se quita de la URL para que recargar no vuelva a entrar
      params.delete('room');
      const query = params.toString();
      window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
      showScreen('connect');
      codeInput.value = roomParam;
      void connectOnline('join');
    } else if (requested === 'training') {
      offlineKind = 'training';
      openOfflineLobby();
    } else if (requested === 'online') {
      showScreen('connect');
    } else if (requested === 'heroes') {
      openGallery();
    } else {
      menuOffline.focus();
    }
  } catch (error) {
    console.error(error);
    setLoadingError(error instanceof Error ? error.message : t('Error cargando recursos'));
    splash.fail();
    loadingFill.style.background = '#ff5a5a';
  }
}

menuOffline.addEventListener('click', openOfflineLobby);
menuOnline.addEventListener('click', () => showScreen('connect'));
function openGallery(): void {
  if (!gallery) return;
  showScreen('gallery');
  gallery.show(selectedHero);
}
menuGallery.addEventListener('click', openGallery);
el('gallery-back').addEventListener('click', () => showScreen('menu'));
el('connect-back').addEventListener('click', () => showScreen('menu'));
el('online-create').addEventListener('click', () => void connectOnline('create'));
el('online-join').addEventListener('click', () => void connectOnline('join'));
el('online-quick').addEventListener('click', () => void connectOnline('quick'));
codeInput.addEventListener('keydown', (event) => {
  if (event.code === 'Enter') void connectOnline('join');
});
el('lobby-back').addEventListener('click', () => {
  if (lobbyKind === 'online') leaveOnlineLobby();
  else showScreen('menu');
});
startButton.addEventListener('click', start);
restartButton.addEventListener('click', () => {
  // offline: vuelta al lobby para elegir otra partida
  game.setPaused(false);
  el('match-end').hidden = true;
  showOverlay('lobby');
  openOfflineLobby();
});

// ---------------------------------------------------------------- pausa (P)
const pauseMenu = document.getElementById('pause-menu');
const pauseNote = document.getElementById('pause-note');

// botón de pausa de los controles táctiles
game.onPauseRequest = () => {
  if (settingsOpen() || !started || game.isMatchEnded) return;
  setPause(true);
};

function setPause(open: boolean): void {
  if (!pauseMenu) return;
  if (open) emoteWheel.hide();
  pauseMenu.hidden = !open;
  game.setPaused(open);
  if (pauseNote) pauseNote.textContent = open && game.isOnline ? t('Online: la partida sigue corriendo en el servidor.') : '';
}

function releaseBrowser(): void {
  window.removeEventListener('beforeunload', confirmLeave);
  const keyboard = (navigator as Navigator & { keyboard?: { unlock?: () => void } }).keyboard;
  keyboard?.unlock?.();
  if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
}

document.getElementById('pause-resume')?.addEventListener('click', () => setPause(false));
document.getElementById('pause-close')?.addEventListener('click', () => setPause(false));
document.getElementById('pause-home')?.addEventListener('click', () => {
  releaseBrowser();
  window.location.reload(); // vuelve a la selección de personaje
});
document.getElementById('pause-quit')?.addEventListener('click', () => {
  releaseBrowser();
  window.close();
  // los navegadores sólo dejan cerrar pestañas abiertas por script: si sigue
  // abierta, se descarga el juego y queda la pestaña en blanco para cerrarla
  window.setTimeout(() => window.location.replace('about:blank'), 150);
});

// ---------------------------------------------------------- configuración
const settingsPanel = document.getElementById('settings');
const sensitivityInput = document.getElementById('setting-sensitivity') as HTMLInputElement | null;
const sensitivityValue = document.getElementById('setting-sensitivity-value');
const SETTINGS_KEY = 'moba.settings';
const MIN_SENSITIVITY = 0.25;
const MAX_SENSITIVITY = 3;

interface GameSettings {
  mouseSensitivity: number;
}

function loadSettings(): GameSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { mouseSensitivity: 1 };
    const parsed = JSON.parse(raw) as Partial<GameSettings>;
    const value = Number(parsed.mouseSensitivity);
    return {
      mouseSensitivity: Number.isFinite(value)
        ? Math.min(MAX_SENSITIVITY, Math.max(MIN_SENSITIVITY, value))
        : 1,
    };
  } catch {
    return { mouseSensitivity: 1 };
  }
}

function saveSettings(settings: GameSettings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // sin almacenamiento (modo privado): el ajuste dura sólo la sesión
  }
}

let settings = loadSettings();

function applySettings(): void {
  game.setMouseSensitivity(settings.mouseSensitivity);
  if (sensitivityInput) sensitivityInput.value = String(settings.mouseSensitivity);
  if (sensitivityValue) sensitivityValue.textContent = `${Math.round(settings.mouseSensitivity * 100)}%`;
}

// ---------------------------------------------------------------- idioma
const languageSelect = document.getElementById('setting-language') as HTMLSelectElement | null;
const languageNote = document.getElementById('setting-language-note');

/** El idioma se cambia recargando: en partida u online se bloquea para no echarte de la sala. */
function refreshLanguageControl(): void {
  if (!languageSelect) return;
  languageSelect.value = LANG;
  const locked = started || Boolean(net);
  languageSelect.disabled = locked;
  if (languageNote) {
    languageNote.textContent = locked ? t('Sólo se puede cambiar desde el menú principal') : t('Se recarga el menú para aplicarlo');
  }
}

languageSelect?.addEventListener('change', () => {
  const lang: Lang = languageSelect.value === 'es' ? 'es' : 'en';
  if (lang === LANG || started || net) return;
  saveLanguage(lang);
  window.removeEventListener('beforeunload', confirmLeave);
  window.location.reload();
});

function setSettingsOpen(open: boolean): void {
  if (!settingsPanel) return;
  if (open) refreshLanguageControl();
  settingsPanel.hidden = !open;
  if (open) document.getElementById('settings-close')?.focus();
}

document.getElementById('settings-open-start')?.addEventListener('click', () => setSettingsOpen(true));
document.getElementById('settings-open-pause')?.addEventListener('click', () => setSettingsOpen(true));
document.getElementById('settings-close')?.addEventListener('click', () => setSettingsOpen(false));
document.getElementById('settings-x')?.addEventListener('click', () => setSettingsOpen(false));
// clic fuera de la tarjeta = cerrar (configuración y pausa)
document.getElementById('settings')?.addEventListener('click', (event) => {
  if (event.target === event.currentTarget) setSettingsOpen(false);
});
document.getElementById('setting-fullscreen')?.addEventListener('click', () => {
  setFullscreen(!document.fullscreenElement);
});
document.addEventListener('fullscreenchange', refreshFullscreenButton);
refreshFullscreenButton();
document.getElementById('settings-reset')?.addEventListener('click', () => {
  settings = { mouseSensitivity: 1 };
  applySettings();
  saveSettings(settings);
  game.audio.resetVolumes();
});
sensitivityInput?.addEventListener('input', () => {
  const value = Number(sensitivityInput.value);
  if (!Number.isFinite(value)) return;
  settings.mouseSensitivity = Math.min(MAX_SENSITIVITY, Math.max(MIN_SENSITIVITY, value));
  applySettings();
  saveSettings(settings);
});
applySettings();

// ------------------------------------------------------------------ sonido
const audio = game.audio;
const volumeInputs = Array.from(document.querySelectorAll<HTMLInputElement>('.setting-volume'));
const muteInput = document.getElementById('setting-mute') as HTMLInputElement | null;
const audioToggle = document.getElementById('audio-toggle');

/** Refleja en Configuración y en el botón del menú el volumen y el silencio actuales. */
function refreshAudioUi(): void {
  const volumes = audio.getVolumes();
  for (const input of volumeInputs) {
    const channel = input.dataset.channel as keyof typeof volumes;
    const percent = Math.round(volumes[channel] * 100);
    if (document.activeElement !== input) input.value = String(percent);
    const label = document.getElementById(`${input.id}-value`);
    if (label) label.textContent = `${percent}%`;
  }
  if (muteInput) muteInput.checked = audio.isMuted;
  if (audioToggle) {
    audioToggle.classList.toggle('muted', audio.isMuted);
    audioToggle.setAttribute('aria-pressed', String(audio.isMuted));
    const label = audioToggle.querySelector('.audio-label');
    if (label) label.textContent = audio.isMuted ? t('SILENCIADO') : t('SONIDO');
  }
}

for (const input of volumeInputs) {
  input.addEventListener('input', () => {
    const channel = input.dataset.channel as Parameters<typeof audio.setVolume>[0];
    audio.setVolume(channel, Number(input.value) / 100);
  });
}
muteInput?.addEventListener('change', () => audio.setMuted(muteInput.checked));
audioToggle?.addEventListener('click', () => audio.toggleMute());
audio.onChange = refreshAudioUi;
refreshAudioUi();

function settingsOpen(): boolean {
  return settingsPanel ? !settingsPanel.hidden : false;
}

window.addEventListener('keydown', (event) => {
  if (event.code === 'Escape' && settingsOpen()) {
    event.preventDefault();
    setSettingsOpen(false);
    return;
  }
  if (event.code !== 'KeyP' || event.repeat || event.ctrlKey || event.shiftKey) return;
  if (settingsOpen() || !started || game.isMatchEnded) return;
  event.preventDefault();
  setPause(pauseMenu ? pauseMenu.hidden === true : false);
});
window.addEventListener('keydown', (event) => {
  if (event.code === 'Enter' || event.code === 'NumpadEnter') {
    if (settingsOpen() || started || document.activeElement instanceof HTMLInputElement) return;
    start();
  }
});

// Móvil: al pulsar cualquier botón del menú se pasa a pantalla completa en
// horizontal (los navegadores sólo lo permiten dentro de un gesto del usuario).
if (document.body.classList.contains('touch')) {
  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element) || !event.target.closest('button')) return;
      if (document.fullscreenElement || !document.documentElement.requestFullscreen) return;
      document.documentElement
        .requestFullscreen({ navigationUI: 'hide' })
        .then(() => (screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> }).lock?.('landscape'))
        .catch(() => {});
    },
    true,
  );
}

void boot();
